import { useState, useEffect } from "react";
import { Lock, GripVertical, ChevronUp, ChevronDown, Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ListAvailableColumnsModal } from "./ListAvailableColumnsModal";

type FieldConfig = {
  fieldname: string;
  label: string;
  required?: boolean;
};

export type SystemColumnToggle = {
  id: string;
  label: string;
  icon: React.ReactNode;
  visible: boolean;
};

type ListSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: FieldConfig[];
  visibleFieldOrder: string[];
  onChangeVisibleFieldOrder: (order: string[]) => void;
  onToggleField: (fieldname: string, enabled: boolean) => void; // Keep for future use, but not called during dialog
  isLoading?: boolean; // Loading state for meta-driven fields
  systemColumns?: SystemColumnToggle[];
  onSystemColumnToggle?: (id: string, visible: boolean) => void;
};

export function ListSettingsModal({
  open,
  onOpenChange,
  fields,
  visibleFieldOrder,
  onChangeVisibleFieldOrder,
  // onToggleField - not used during dialog, but kept for API compatibility
  isLoading = false,
  systemColumns,
  onSystemColumnToggle,
}: ListSettingsModalProps) {
  // Local state only - changes are not persisted until Save
  const [localOrder, setLocalOrder] = useState(visibleFieldOrder);
  const [localSystemVisibility, setLocalSystemVisibility] = useState<Record<string, boolean>>({});
  const [isAvailableModalOpen, setIsAvailableModalOpen] = useState(false);

  // Sync local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalOrder(visibleFieldOrder);
      if (systemColumns) {
        const vis: Record<string, boolean> = {};
        for (const col of systemColumns) {
          vis[col.id] = col.visible;
        }
        setLocalSystemVisibility(vis);
      }
    }
  }, [open, visibleFieldOrder, systemColumns]);

  const moveField = (fieldname: string, direction: "up" | "down") => {
    const index = localOrder.indexOf(fieldname);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= localOrder.length) return;

    const newOrder = [...localOrder];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    setLocalOrder(newOrder);
  };

  const handleSave = () => {
    // Commit changes to store only on Save
    onChangeVisibleFieldOrder(localOrder);
    // Persist system column visibility changes
    if (onSystemColumnToggle && systemColumns) {
      for (const col of systemColumns) {
        const newVis = localSystemVisibility[col.id] ?? col.visible;
        if (newVis !== col.visible) {
          onSystemColumnToggle(col.id, newVis);
        }
      }
    }
    onOpenChange(false);
  };


  const visibleFields = localOrder
    .map(fieldname => fields.find(f => f.fieldname === fieldname))
    .filter((f): f is FieldConfig => !!f);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 flex flex-col overflow-hidden">
        {/* Checkmark button - top right */}
        <Button
          type="button"
          onClick={handleSave}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-apple-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">Save</span>
        </Button>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative">
            <DialogHeader className="px-10 pt-20 pb-4">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px]">
                List Settings
              </DialogTitle>
            </DialogHeader>

            <div className="pb-8 px-10 pt-2">
              <div className="space-y-6">
          {/* Loading state */}
          {isLoading && fields.length === 0 && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              Loading fields...
            </div>
          )}
          
          {/* Visible Columns (Reorderable) */}
          {!isLoading && (
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="text-sm font-medium">Visible Columns</h3>
                <Button
                  variant="outline"
                  onClick={() => setIsAvailableModalOpen(true)}
                  className="h-11 px-5 text-sm bg-[#FFCC00] hover:bg-[#FFB800] active:bg-[#FFA500] text-gray-900 dark:text-gray-900 border-0 hover:text-gray-900 dark:hover:text-gray-900 font-medium rounded-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add / Remove columns
                </Button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                {visibleFields.length === 0 ? (
                  <div className="flex items-center justify-center min-h-[52px] px-5 text-muted-foreground text-sm">
                    No columns visible. Click "Add / Remove columns" to add some.
                  </div>
                ) : (
                  visibleFields.map((field, index) => (
                    <div
                      key={field.fieldname}
                      className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing" />
                        <Label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                          {field.label}
                        </Label>
                        {field.required && (
                          <Lock className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveField(field.fieldname, "up")}
                          disabled={index === 0 || field.fieldname.startsWith("__")}
                          className="h-8 w-8 rounded-sm hover:bg-gray-200/50 dark:hover:bg-gray-800/50"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveField(field.fieldname, "down")}
                          disabled={index === visibleFields.length - 1 || field.fieldname.startsWith("__")}
                          className="h-8 w-8 rounded-sm hover:bg-gray-200/50 dark:hover:bg-gray-800/50"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
              {/* System Columns (Toggle on/off) */}
              {systemColumns && systemColumns.length > 0 && (
                <div>
                  <div className="flex items-center mb-3 px-1">
                    <h3 className="text-sm font-medium">System Columns</h3>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                    {systemColumns.map((col) => (
                      <div
                        key={col.id}
                        className="flex items-center justify-between min-h-[52px] px-5 border-b border-gray-200/40 dark:border-gray-700/40 last:border-b-0"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-muted-foreground">{col.icon}</span>
                          <Label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                            {col.label}
                          </Label>
                        </div>
                        <Switch
                          checked={localSystemVisibility[col.id] ?? col.visible}
                          onCheckedChange={(checked) => {
                            setLocalSystemVisibility((prev) => ({
                              ...prev,
                              [col.id]: checked,
                            }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Bottom fade-out gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>
          
          {/* Sticky Save Button */}
          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="button"
              onClick={handleSave}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </Button>
          </div>
        </div>

        {/* Secondary modal for Available Columns */}
        {!isLoading && (
          <ListAvailableColumnsModal
            open={isAvailableModalOpen}
            onOpenChange={setIsAvailableModalOpen}
            fields={fields}
            selectedFieldnames={localOrder}
            onSave={(nextVisibleFieldnames) => {
              // Only update localOrder here, not the global store
              setLocalOrder(nextVisibleFieldnames);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

